/*
 * Copyright (c) 2019 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import moment from 'moment';
import { CoreErrorKey } from '../key/core-error.key';
import { ApplicationError } from '../model/application-error.model';

export interface OptionInfo {
  type?: string;
  default?: any;
  convert?: (value: any) => any;
  values?: any[];
}

export function setupOptions<T extends { [key: string]: any }>(
  optionInfos: { [key: string]: OptionInfo | ((value: any) => any) | any },
  ...originalOptions: T[]
): T {
  const options = {};
  Object.keys(optionInfos).forEach((optionKey: string) => {
    const optionInfo: OptionInfo | any = optionInfos[optionKey];
    let optionType: string;
    let defaultValue: any;
    let customConvertOption: (value: any) => any;
    let optionValues: any[];
    if (isOptionInfo(optionInfo)) {
      optionType = optionInfo.type !== undefined ? optionInfo.type : typeof optionInfo.default;
      defaultValue = optionInfo.default;
      customConvertOption = optionInfo.convert;
      optionValues = optionInfo.values;
    } else if (typeof optionInfo === 'function') {
      customConvertOption = optionInfo;
    } else {
      optionType = typeof optionInfo;
      defaultValue = optionInfo;
      customConvertOption = null;
      optionValues = null;
    }
    let optionValue: any;
    if (optionType === 'object' && defaultValue && !Array.isArray(defaultValue)) {
      optionValue = setupOptions(defaultValue, ...originalOptions.map((originalOptionsItem) => {
        if (!originalOptionsItem) {
          return undefined;
        }
        if (typeof originalOptionsItem !== 'object') {
          throw new ApplicationError({
            key: CoreErrorKey.OPTIONS_INVALID,
            arguments: { options: originalOptionsItem },
            message: `Options '${originalOptionsItem}' invalid: 'object' type expected`
          });
        }
        return originalOptionsItem && originalOptionsItem[optionKey];
      }));
    } else {
      for (let i = originalOptions.length - 1; i >= 0; i--) {
        if (!originalOptions[i]) {
          continue;
        }
        if (typeof originalOptions[i] !== 'object') {
          throw new ApplicationError({
            key: CoreErrorKey.OPTIONS_INVALID,
            arguments: { options: originalOptions[i] },
            message: `Options '${originalOptions[i]}' invalid: 'object' type expected`
          });
        }
        if (optionKey in originalOptions[i]) {
          optionValue = originalOptions[i][optionKey];
          break;
        }
      }
    }
    if (optionValue === undefined) {
      optionValue = defaultValue;
    }
    optionValue = customConvertOption
      ? customConvertOption(optionValue)
      : convertOption(optionKey, optionValue, optionType);
    if (optionValues && optionValues.indexOf(optionValue) < 0) {
      throw new ApplicationError({
        key: CoreErrorKey.OPTION_INVALID,
        arguments: { key: optionKey, value: optionValue },
        message: `Option '${optionKey}' invalid`
      });
    }
    options[optionKey] = optionValue;
  });
  return options as T;
}

function isOptionInfo(value: any): boolean {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const keys = Object.keys(value);
  if (keys.length === 0) {
    return false;
  }
  for (const key of Object.keys(value)) {
    if (key !== 'type' && key !== 'default' && key !== 'values' && key !== 'convert') {
      return false;
    }
  }
  return true;
}

function convertOption(key: string, value: any, type: string): any {
  if (!value || !type || typeof value === type) {
    return value;
  }
  switch (type) {
    case 'boolean':
      return toBoolean(key, value);
    case 'number':
      return toNumber(key, value);
    case 'date':
      return toDate(key, value);
    case 'object':
      return toObject(key, value);
    case 'string':
      return toString(value);
    default:
      throw new Error(`Type conversion to '${type}' not supported`);
  }
}

function toString(value: any): string {
  return typeof value === 'string' ? value : JSON.stringify(value);
}

function toBoolean(key: string, value: any): boolean {
  const booleanMatch = /^\s*(true|1)|(false|0)\s*$/i.exec(toString(value));
  if (!booleanMatch) {
    throw new ApplicationError({
      key: CoreErrorKey.OPTION_INVALID,
      arguments: { key, value },
      message: `Option '${key}' invalid`
    });
  }
  return !booleanMatch[2];
}

function toNumber(key: string, value: string): number {
  const numberValue = parseFloat(toString(value));
  if (Number.isNaN(numberValue)) {
    throw new ApplicationError({
      key: CoreErrorKey.OPTION_INVALID,
      arguments: { key, value },
      message: `Option '${key}' invalid`
    });
  }
  return numberValue;
}

function toDate(key: string, value: any): Date {
  const momentDate = moment(value);
  if (!momentDate.isValid()) {
    throw new ApplicationError({
      key: CoreErrorKey.OPTION_INVALID,
      arguments: { key, value },
      message: `Option '${key}' invalid`
    });
  }
  return momentDate.toDate();
}

function toObject(key: string, value: any): object {
  try {
    return JSON.parse(toString(value));
  } catch {
    throw new ApplicationError({
      key: CoreErrorKey.OPTION_INVALID,
      arguments: { key, value },
      message: `Option '${key}' invalid`
    });
  }
}
